import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import SEO from "../components/SEO";
import { getPageSeo, breadcrumbSchema, caseStudySchema } from "../lib/seo";

const OasizMallCaseStudy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-[#101F27] text-white min-h-screen">
      <SEO
        {...getPageSeo("/case-studies/oasiz-mall")}
        schema={[
          caseStudySchema("/case-studies/oasiz-mall"),
          breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "Case Studies", path: "/case-studies" },
          { name: "Oasiz Mall", path: "/case-studies/oasiz-mall" },
        ]),
        ]}
      />
      {/* Page Title Bar */}
      <section className="bg-[#37464F]/30 border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-center">
            <div className="lg:col-span-2">
              <Link
                to="/case-studies"
                className="inline-flex items-center text-[#009688] hover:underline mb-6 text-sm font-semibold"
                data-testid="back-to-case-studies-link"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Case Studies
              </Link>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight">
                Revolutionizing Retail: How Oasiz Mall, Spain's Largest Shopping Centre, Successfully Integrated WristCoin
              </h1>
            </div>
            <div className="lg:col-span-3">
              <div
                className="w-full h-72 md:h-96 rounded-lg bg-cover bg-center shadow-2xl"
                style={{
                  backgroundImage:
                    "url('/images/clients/Oasiz Madrid.png')",
                }}
                role="img"
                aria-label="Oasiz Mall Madrid"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Case Study Content */}
      <section className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <article className="prose prose-invert max-w-none">
            <p className="text-lg text-gray-300 leading-relaxed mb-10">
              Oasiz Mall in Madrid opened in December 2021 as no ordinary mall, but the largest shopping center in Spain. Much more than just shopping, Oasiz offers more than 1.6 million square feet (250,000 square meters) of attractions, fitness centers, dining options, and even an indoor beach.
            </p>

            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Challenges</h2>
            <p className="text-lg text-gray-300 leading-relaxed mb-10">
              The developers of Oasiz Mall came to us looking for a closed-loop cashless system for various activities in the mall including some water activities in the 12,500 square meter lake. The activities run year-round and Oasiz needed it to be easy for the guests to top up and with the flexibility to use their own mobile device (self-serve) or visit a staff member at the mall and top up with cash.
            </p>

            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Solution</h2>
            <p className="text-lg text-gray-300 leading-relaxed mb-10">
              WristCoin's exclusive hybrid mode top-up wristbands were the ideal solution. This gives guests the option to self-serve using their own mobile device or with a staff member at the mall using cash using a single wristband! Our industry-leading no app, no username, and no password guest experience won them over. Since guests could check their balance at any time using their mobile phone there was no need for elaborate kiosks to check balances, top-up up the account, or view past transactions. With direct banking integration in many European countries including Spain, we configured the cashless system to process payments using the customer's merchant account with payouts going directly to its bank account without WristCoin as an intermediary.
            </p>

            <div className="relative w-full mb-10 rounded-lg overflow-hidden shadow-2xl" style={{ paddingTop: "56.25%" }}>
              <iframe
                className="absolute inset-0 w-full h-full"
                src="https://www.youtube-nocookie.com/embed/JaYMuT79K8s"
                title="[4K] Oasiz Madrid | Recorrido por el nuevo centro comercial más grande de España"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              ></iframe>
            </div>

            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Outcome</h2>
            <p className="text-lg text-gray-300 leading-relaxed mb-6">
              <a
                href="https://oasizmadrid.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#009688] hover:underline"
              >
                Oasiz Mall
              </a>{" "}
              has been operating the WristCoin cashless system since its launch in 2021 without interruption. In the first month of opening thousands of wristbands were activated. With a fleet of mobile point-of-sale terminals Oasiz is able to smoothly operate the various activities in the mall.
            </p>
          </article>
        </div>
      </section>
    </div>
  );
};

export default OasizMallCaseStudy;
